import { Box, Select, Skeleton, Table, Text } from "@mantine/core";
import dayjs from "dayjs";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import useSwr from "swr";
import { LedgerApi } from "../../../apis";
import { IAccountStatementReport, IAccountsResponse } from "../../../apis/ledger";
import { Page, PageHeader } from "../../../components/layout/Page";
import { DateRangeChip } from "../../../components/list";
import { Notice } from "../../../components/ui/notice";
import { message } from "../../../utils/Message";
import { Amt, money } from "./report-format";
import { saveBlob } from "./report-download";
import { parsePeriod, periodBounds } from "./report-period";
import { ExportButtons } from "./ReportShell";
import classes from "./ReportShell.module.css";
import { ListStates } from "../../../components/list";

const COLS = 6;
const LIMIT = 500;
const YMD = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Дансны хуулга (SPRINT-05 §6.4) — нэг дансны хөдөлгөөн, гүйлгээ бүрийн дараах
 * үлдэгдэлтэй. `?accountId=&dateFrom=&dateTo=`-г уншиж, бичдэг; огноо байхгүй бол
 * `?period=YYYY-MM`-ийн сар.
 */
export const LedgerAccountStatementPage = () => {
  const { t } = useTranslation(["billing", "common"]);
  const [params, setParams] = useSearchParams();
  const [exporting, setExporting] = useState(false);

  const accountId = params.get("accountId");
  const fromParam = params.get("dateFrom");
  const toParam = params.get("dateTo");
  const period = parsePeriod(params.get("period"));
  const fallback = useMemo(() => periodBounds(period ?? { year: new Date().getFullYear(), month: new Date().getMonth() + 1 }), [period]);
  const dateFrom = fromParam && YMD.test(fromParam) ? fromParam : fallback.from;
  const dateTo = toParam && YMD.test(toParam) ? toParam : fallback.to;

  const patch = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
  };

  const { data: accountsData, error: accountsError } = useSwr<IAccountsResponse>(
    "ledger.accounts",
    async () => await LedgerApi.getAccounts(),
    { revalidateOnFocus: false },
  );
  const accounts = useMemo(() => accountsData?.accounts ?? [], [accountsData]);

  const ready = Boolean(accountId);
  const { data, isLoading, error } = useSwr<IAccountStatementReport>(
    ready ? `ledger.report.accountStatement.${accountId}.${dateFrom}.${dateTo}` : null,
    async () => await LedgerApi.getAccountStatement({ accountId: accountId!, dateFrom, dateTo, limit: LIMIT }),
    { revalidateOnFocus: false },
  );

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const totals = useMemo(
    () =>
      rows.reduce(
        (s, r) => ({ debit: s.debit + (r.isDebit ? Number(r.amount) : 0), credit: s.credit + (r.isDebit ? 0 : Number(r.amount)) }),
        { debit: 0, credit: 0 },
      ),
    [rows],
  );

  const exportExcel = async () => {
    if (!accountId) return;
    setExporting(true);
    try {
      saveBlob(
        await LedgerApi.exportReportExcel({ report: "account-statement", accountId, dateFrom, dateTo, limit: LIMIT }),
        `account-statement-${dateFrom}-${dateTo}.xlsx`,
      );
    } catch (err) {
      message.error((err as Error).message);
    } finally {
      setExporting(false);
    }
  };

  const showSkeleton = ready && isLoading;

  return (
    <Page>
      <PageHeader
        title={t("billing:ledger.report.statementTitle")}
        subtitle={t("billing:ledger.report.statementSubtitle")}
        extra={<ExportButtons onExport={{ onExcel: exportExcel, excelLoading: exporting }} />}
      />

      {accountsError && (
        <Notice tone="danger" role="alert" title={t("billing:ledger.report.accountsLoadError")} />
      )}

      <Box className={classes.headerRight}>
        <Select
          value={accountId}
          onChange={(v) => patch({ accountId: v })}
          data={accounts.map((a) => ({ value: a.accountId, label: `${a.code} · ${a.name}` }))}
          placeholder={accountsError ? t("billing:ledger.report.accountsLoadError") : t("billing:ledger.report.accountPlaceholder")}
          disabled={Boolean(accountsError)}
          nothingFoundMessage={t("common:noResultsShort")}
          searchable
          clearable
          w={{ base: "100%", sm: 380 }}
          aria-label={t("billing:ledger.colAccount")}
        />
        <DateRangeChip
          label={t("billing:ledger.datePlaceholder")}
          value={[dateFrom, dateTo]}
          onChange={([from, to]) =>
            patch({
              dateFrom: from ? dayjs(from).format("YYYY-MM-DD") : null,
              dateTo: to ? dayjs(to).format("YYYY-MM-DD") : null,
              period: null,
            })
          }
          clearable={false}
        />
      </Box>

      {!ready ? (
        <Box className={classes.card}>
          <Box className={classes.state}>
            <Text className={classes.stateTitle}>{t("billing:ledger.report.pickAccount")}</Text>
            <Text size="sm">{t("billing:ledger.report.pickAccountHint")}</Text>
            <Link to="/ledger/accounts" className={classes.linkBtn}>{t("billing:ledger.report.toAccounts")}</Link>
          </Box>
        </Box>
      ) : error ? (
        <Notice tone="danger" role="alert" title={t("billing:ledger.report.loadError")} />
      ) : (
        <>
          {data?.truncated && (
            <Notice title={t("billing:ledger.report.truncatedWarning", { limit: LIMIT })} />
          )}

          <Box className={classes.kpi}>
            <Box className={classes.kpiCell}>
              <Text span className={classes.kpiLabel}>{t("billing:ledger.report.colOpening")}</Text>
              <Text span className={classes.kpiValue}>{data ? money(data.openingBalance) : "-"}</Text>
            </Box>
            <Box className={classes.kpiCell}>
              <Text span className={classes.kpiLabel}>{t("billing:ledger.colDebit")}</Text>
              <Text span className={classes.kpiValue}>{data ? `+${money(totals.debit)}` : "-"}</Text>
            </Box>
            <Box className={classes.kpiCell}>
              <Text span className={classes.kpiLabel}>{t("billing:ledger.colCredit")}</Text>
              <Text span className={classes.kpiValue}>{data ? `−${money(totals.credit)}` : "-"}</Text>
            </Box>
            <Box className={classes.kpiCell}>
              <Text span className={classes.kpiLabel}>{t("billing:ledger.report.colClosing")}</Text>
              <Text span className={`${classes.kpiValue} ${classes.strong}`}>{data ? `${money(data.closingBalance)} ₮` : "-"}</Text>
            </Box>
          </Box>

          <Box className={classes.card}>
            <Box className={classes.wrap}>
              {showSkeleton ? (
                <ListStates loading empty columns={COLS} />
              ) : (
              <Table className={classes.table} withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("billing:ledger.colDate")}</Table.Th>
                    <Table.Th>{t("billing:ledger.report.colEntry")}</Table.Th>
                    <Table.Th>{t("billing:ledger.colDescription")}</Table.Th>
                    <Table.Th className={classes.num}>{t("billing:ledger.colDebit")}</Table.Th>
                    <Table.Th className={classes.num}>{t("billing:ledger.colCredit")}</Table.Th>
                    <Table.Th className={classes.num}>{t("billing:ledger.colBalance")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(
                    <Fragment>
                      <Table.Tr className={classes.groupRow}>
                        <Table.Td colSpan={5}>{t("billing:ledger.report.openingOn", { date: dateFrom })}</Table.Td>
                        <Table.Td className={`${classes.num} ${classes.strong}`}>{money(data?.openingBalance ?? 0)}</Table.Td>
                      </Table.Tr>
                      {rows.length === 0 ? (
                        <Table.Tr>
                          <Table.Td colSpan={COLS} p={0}>
                            <Box className={classes.state}>
                              <Text className={classes.stateTitle}>{t("billing:ledger.report.noMovement")}</Text>
                            </Box>
                          </Table.Td>
                        </Table.Tr>
                      ) : (
                        rows.map((row, i) => (
                          <Table.Tr key={`${row.entryId}-${i}`}>
                            <Table.Td>{row.entryDate}</Table.Td>
                            <Table.Td>
                              <Link className={classes.codeLink} to={`/ledger/journal?entry=${row.entryId}`}>{row.entryNumber}</Link>
                            </Table.Td>
                            <Table.Td>{row.description?.trim() || <Text span className={classes.muted}>-</Text>}</Table.Td>
                            <Table.Td className={classes.num}><Amt value={row.isDebit ? row.amount : null} /></Table.Td>
                            <Table.Td className={classes.num}><Amt value={row.isDebit ? null : row.amount} /></Table.Td>
                            <Table.Td className={`${classes.num} ${classes.strong}`}>{money(row.balance)}</Table.Td>
                          </Table.Tr>
                        ))
                      )}
                    </Fragment>
                  )}
                </Table.Tbody>
                {data && !showSkeleton && (
                  <Table.Tfoot>
                    <Table.Tr className={classes.grandRow}>
                      <Table.Td colSpan={3}>{t("billing:ledger.report.closingOn", { date: dateTo })}</Table.Td>
                      <Table.Td className={classes.num}>{money(totals.debit)}</Table.Td>
                      <Table.Td className={classes.num}>{money(totals.credit)}</Table.Td>
                      <Table.Td className={classes.num}>{money(data.closingBalance)} ₮</Table.Td>
                    </Table.Tr>
                  </Table.Tfoot>
                )}
              </Table>
              )}
            </Box>
          </Box>
        </>
      )}
    </Page>
  );
};

export default LedgerAccountStatementPage;
